import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Col,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { API_ENDPOINTS } from '../../api/config';
import { del, get, post, put } from '../../api/request';

interface AuthorItem {
  id: string;
  name: string;
  level: '殿堂作家' | '金番作家';
  work: string;
  avatar_url: string;
  sort: number;
  enabled: boolean;
}

interface AuthorFormValues extends Omit<AuthorItem, 'id'> {
  gender?: '不限' | '男' | '女';
  age?: string;
  extra_prompt?: string;
}

const AuthorManagementPage: React.FC = () => {
  const [rows, setRows] = useState<AuthorItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [editing, setEditing] = useState<AuthorItem | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [form] = Form.useForm<AuthorFormValues>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await get<{ data: AuthorItem[] }>(API_ENDPOINTS.operations.libraryAuthors);
      setRows(result.data || []);
    } catch (error: any) {
      message.error(error.message || '作家列表加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const showEditor = (record?: AuthorItem) => {
    setEditing(record || null);
    form.setFieldsValue(record ? { ...record, gender: '不限', age: '30-45岁' } : {
      name: '',
      level: '殿堂作家',
      work: '',
      avatar_url: '',
      sort: rows.length + 1,
      enabled: true,
      gender: '不限',
      age: '30-45岁',
    });
    setOpen(true);
  };

  const save = async () => {
    const values = await form.validateFields();
    const payload = {
      name: values.name,
      level: values.level,
      work: values.work,
      avatar_url: values.avatar_url || '',
      sort: values.sort,
      enabled: values.enabled,
    };
    setSaving(true);
    try {
      if (editing) {
        await put(API_ENDPOINTS.operations.libraryAuthor(editing.id), payload);
      } else {
        await post(API_ENDPOINTS.operations.libraryAuthors, payload);
      }
      message.success(editing ? '作家信息已更新' : '作家已新增');
      setOpen(false);
      await load();
    } catch (error: any) {
      message.error(error.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const generateAvatar = async () => {
    const values = await form.validateFields(['name', 'level', 'work', 'gender', 'age', 'extra_prompt']);
    setGenerating(true);
    try {
      const result = await post<{ avatar_url: string; model: string }>(
        API_ENDPOINTS.operations.generateAuthorAvatar,
        values,
      );
      form.setFieldValue('avatar_url', result.avatar_url);
      message.success(`真人头像已生成（${result.model}）`);
    } catch (error: any) {
      message.error(error.message || '头像生成失败');
    } finally {
      setGenerating(false);
    }
  };

  const toggle = async (record: AuthorItem, enabled: boolean) => {
    try {
      await put(API_ENDPOINTS.operations.libraryAuthor(record.id), { enabled });
      setRows((current) => current.map((item) => item.id === record.id ? { ...item, enabled } : item));
      message.success(enabled ? '已启用' : '已停用');
    } catch (error: any) {
      message.error(error.message || '状态更新失败');
    }
  };

  const remove = async (id: string) => {
    try {
      await del(API_ENDPOINTS.operations.libraryAuthor(id));
      message.success('已删除');
      await load();
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  const batchRemove = async () => {
    try {
      await post(API_ENDPOINTS.operations.batchDeleteAuthors, { ids: selectedKeys });
      setSelectedKeys([]);
      message.success('批量删除完成');
      await load();
    } catch (error: any) {
      message.error(error.message || '批量删除失败');
    }
  };

  const columns: ColumnsType<AuthorItem> = [
    {
      title: '头像',
      dataIndex: 'avatar_url',
      width: 86,
      render: (url: string, record) => url
        ? <Image src={url} width={54} height={54} style={{ objectFit: 'cover', borderRadius: 8 }} />
        : <Avatar size={54}>{record.name.slice(0, 1)}</Avatar>,
    },
    { title: '笔名', dataIndex: 'name', width: 160 },
    { title: '级别', dataIndex: 'level', width: 130, render: (value) => <Tag color={value === '殿堂作家' ? 'gold' : 'orange'}>{value}</Tag> },
    { title: '简介 / 代表方向', dataIndex: 'work' },
    { title: '排序', dataIndex: 'sort', width: 90 },
    {
      title: '启用',
      dataIndex: 'enabled',
      width: 90,
      render: (enabled, record) => <Switch checked={enabled} onChange={(checked) => void toggle(record, checked)} />,
    },
    {
      title: '操作',
      width: 170,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => showEditor(record)}>编辑</Button>
          <Popconfirm title="确认删除这个作家？" onConfirm={() => void remove(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const avatarUrl = Form.useWatch('avatar_url', form);

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => showEditor()}>新增作家</Button>
        <Popconfirm title={`确认删除选中的 ${selectedKeys.length} 位作家？`} disabled={!selectedKeys.length} onConfirm={() => void batchRemove()}>
          <Button danger icon={<DeleteOutlined />} disabled={!selectedKeys.length}>批量删除</Button>
        </Popconfirm>
        <Button icon={<ReloadOutlined />} onClick={() => void load()}>刷新</Button>
      </Space>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 10 }}
        rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedKeys }}
      />
      <Modal
        title={editing ? '编辑作家' : '新增作家'}
        open={open}
        width={720}
        confirmLoading={saving}
        onOk={() => void save()}
        onCancel={() => setOpen(false)}
        okText="保存"
      >
        <Form form={form} layout="vertical">
          <Row gutter={24} align="top">
            <Col flex="170px" style={{ textAlign: 'center' }}>
              {avatarUrl ? <Image src={avatarUrl} width={160} height={160} style={{ objectFit: 'cover', borderRadius: 12 }} /> : <Avatar size={160}>真人头像</Avatar>}
            </Col>
            <Col flex="1" style={{ minWidth: 0 }}>
              <Row gutter={12}>
                <Col span={10}><Form.Item name="name" label="笔名" rules={[{ required: true }]}><Input placeholder="例如：空留" /></Form.Item></Col>
                <Col span={8}>
                <Form.Item name="level" label="作家级别" rules={[{ required: true }]}>
                  <Select options={[{ value: '殿堂作家' }, { value: '金番作家' }]} />
                </Form.Item>
                </Col>
                <Col span={6}><Form.Item name="sort" label="排序" rules={[{ required: true }]}><InputNumber min={1} max={999} style={{ width: '100%' }} /></Form.Item></Col>
              </Row>
              <Form.Item name="work" label="简介 / 代表方向"><Input placeholder="例如：悬疑幻想代表作者" /></Form.Item>
              <Form.Item name="avatar_url" label="头像地址"><Input.TextArea rows={2} placeholder="可粘贴图片地址，或使用下方 AI 生成" /></Form.Item>
              <Space align="end">
                <Form.Item name="gender" label="形象性别"><Select style={{ width: 100 }} options={['不限', '男', '女'].map((value) => ({ value }))} /></Form.Item>
                <Form.Item name="age" label="年龄观感"><Input style={{ width: 120 }} /></Form.Item>
                <Form.Item name="extra_prompt" label="补充要求"><Input style={{ width: 180 }} placeholder="例如：中式衬衫" /></Form.Item>
                <Form.Item label=" "><Button icon={<RobotOutlined />} loading={generating} onClick={() => void generateAvatar()}>AI 生成真人头像</Button></Form.Item>
              </Space>
              <Form.Item name="enabled" label="前台展示" valuePropName="checked"><Switch checkedChildren="启用" unCheckedChildren="停用" /></Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default AuthorManagementPage;
